import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from '@angular/core';
import type { BadgeContext, BadgeVariant } from '../badge/badge';
import { HxBadge } from '../badge/badge';
import { HxTooltipDirective } from '../tooltip/tooltip';

/** Segment status value type */
export type SegmentStatus = 'completed' | 'not-completed' | 'errored';

/** Segment definition with key and status */
export type SegmentDefinition = {
  /** Unique key/identifier for the segment */
  key: string;
  /** Status of the segment: 'completed' or 'not-completed' */
  status: SegmentStatus;
  /** Optional display label shown in tooltip and accessibility text */
  label?: string;
  /** Optional icon SVG reference for tooltip display */
  icon?: string;
};

/** Progress state for a single pill segment */
export type PillState = {
  /** Zero-based index in sequence */
  index: number;
  /** One-based section number (user-facing) */
  sectionNumber: number;
  /** The segment key */
  key: string;
  /** Visual/functional state */
  state: 'completed' | 'current' | 'incomplete' | 'errored';
  /** CSS class for styling */
  cssClass: string;
  /** Display size (current is larger) */
  displaySize: 'normal' | 'large';
  /** Background color */
  backgroundColor: string;
  /** Border color (current pill only) */
  borderColor?: string;
  /** ARIA label for accessibility */
  ariaLabel: string;
  /** Unique tracking ID */
  trackingId: string;
  /** Tooltip text shown on hover */
  tooltipText: string;
  /** Optional icon SVG reference for tooltip */
  tooltipIcon?: string;
  /** Badge variant for rendering */
  badgeVariant: BadgeVariant;
  /** Badge context for rendering */
  badgeContext: BadgeContext;
};

const SEGMENTED_PROGRESS_EASTER_EGG_STORAGE_KEY = 'symphony.segmentedProgress.easterEgg';

/**
 * Segmented Progress Component
 *
 * Displays multi-section assessment progress using discrete pill segments. Shows completed sections, current position,
 * and remaining sections.
 *
 * @example
 *   ```html
 *   <hx-segmented-progress
 *     [segments]="[
 *       { key: 'step1', status: 'completed' },
 *       { key: 'step2', status: 'completed' },
 *       { key: 'step3', status: 'not-completed' }
 *     ]"
 *     [currentSegment]="'step3'">
 *   </hx-segmented-progress>
 *   ```;
 */
@Component({
  selector: 'hx-segmented-progress',
  standalone: true,
  imports: [CommonModule, HxBadge, HxTooltipDirective],
  templateUrl: './segmented-progress.html',
  styleUrls: ['./segmented-progress.css', './segmented-progress-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-segmented-progress',
    role: 'group',
    '[attr.aria-label]': 'ariaLabel()',
    '[class.hx-segmented-progress--easter-egg]': 'easterEggEnabled()',
  },
})
export class SegmentedProgressComponent {
  /**
   * Array of segment definitions with key and status. Each segment has a unique key and a status ('completed' or
   * 'not-completed').
   *
   * @example
   *   [
   *     { key: 'personal-info', status: 'completed' },
   *     { key: 'medical-history', status: 'completed' },
   *     { key: 'review', status: 'not-completed' },
   *   ];
   *
   * @default [ ]
   */
  /** Whether the Pac-Man Easter egg is active. */
  protected readonly easterEggEnabled = signal(
    typeof localStorage !== 'undefined' && localStorage.getItem(SEGMENTED_PROGRESS_EASTER_EGG_STORAGE_KEY) === 'true'
  );

  /** Travel duration in seconds — scales so Pac-Man speed feels consistent regardless of pill count. */
  protected readonly pacmanDuration = computed(() => Math.max(2, this.totalSegments() * 0.25));

  readonly segments = input<SegmentDefinition[]>([]);

  /**
   * Key of the current active segment. This determines which segment is highlighted as "current".
   *
   * @example
   *   'medical-history';
   *
   * @default undefined
   */
  readonly currentSegment = input<string | undefined>(undefined);

  /** Emits when a segment pill is clicked. */
  readonly segmentClick = output<{ key: string; index: number }>();

  /** Computed total number of segments */
  protected readonly totalSegments = computed(() => this.segments().length);

  /** Computed number of completed segments */
  protected readonly completedCount = computed(() => this.segments().filter((s) => s.status === 'completed').length);

  /** Computed percentage (0-100) */
  protected readonly percentage = computed(() => {
    const total = this.totalSegments();
    const completed = this.completedCount();
    if (total === 0) {
      return 0;
    }
    const pct = Math.round((completed / total) * 100);
    return isNaN(pct) ? 0 : Math.min(100, Math.max(0, pct));
  });

  /** Computed progress text formatted as "X/Y (P%)" */
  protected readonly progressText = computed(() => {
    const completed = this.completedCount();
    const total = this.totalSegments();
    const pct = this.percentage();
    return `${completed}/${total} (${pct}%)`;
  });

  /** Computed array of pill states for rendering */
  protected readonly pills = computed(() => {
    return this.calculatePillStates();
  });

  /** Computed validation state */
  protected readonly isValid = computed(() => {
    return this.validate();
  });

  /** Computed scale factor based on section count */
  protected readonly scaleFactor = computed(() => {
    const total = this.totalSegments();
    if (total <= 6) {
      return 1.5;
    }
    if (total <= 12) {
      return 1.4;
    }
    return 1.3;
  });

  /** Computed ARIA label for accessibility */
  protected readonly ariaLabel = computed(() => {
    const completed = this.completedCount();
    const total = this.totalSegments();
    const current = this.currentSegment();
    const pct = this.percentage();
    const segmentText = current || 'none';
    return `Progress: ${completed} of ${total} sections complete, ${pct} percent, currently on segment ${segmentText}`;
  });

  /** Calculate pill states for all segments */
  private calculatePillStates(): PillState[] {
    const segmentsList = this.segments();
    const currentKey = this.currentSegment();

    return segmentsList.map((segment, index) => {
      const sectionNumber = index + 1;
      const state = this.determinePillState(segment, currentKey);
      const segmentDisplayLabel = segment.label || segment.key;

      return {
        index,
        sectionNumber,
        key: segment.key,
        state,
        cssClass:
          state === 'current' && segment.status === 'errored'
            ? 'hx-segmented-progress__pill--current hx-segmented-progress__pill--current-errored'
            : `hx-segmented-progress__pill--${state}`,
        displaySize: state === 'current' ? 'large' : 'normal',
        backgroundColor: this.getPillBackgroundColor(state, segment.status),
        borderColor: state === 'current' ? (segment.status === 'errored' ? '#b91c1c' : '#33666E') : undefined,
        ariaLabel: this.generatePillAriaLabel(segmentDisplayLabel, sectionNumber, state),
        trackingId: `pill-${segment.key}`,
        tooltipText: segmentDisplayLabel,
        tooltipIcon: segment.icon,
        badgeVariant: state === 'completed' ? 'fill' : 'soft',
        badgeContext: state === 'completed' ? 'brand' : 'neutral',
      };
    });
  }

  protected onPillClick(pill: PillState): void {
    this.segmentClick.emit({ key: pill.key, index: pill.index });
  }

  /** Build sprite href for tooltip icon, normalized to ccs-* symbol names. */
  protected getTooltipIconHref(iconName: string | undefined): string | null {
    if (!iconName) {
      return null;
    }

    const normalizedIconName = iconName.startsWith('ccs-') ? iconName : `ccs-${iconName}`;
    return `/assets/icons/complexcare-icons.svg#${normalizedIconName}`;
  }

  /** Determine pill state based on segment definition and current key */
  private determinePillState(
    segment: SegmentDefinition,
    currentKey: string | undefined
  ): 'completed' | 'current' | 'incomplete' | 'errored' {
    if (segment.key === currentKey) {
      return 'current';
    }
    if (segment.status === 'errored') {
      return 'errored';
    }
    if (segment.status === 'completed') {
      return 'completed';
    }
    return 'incomplete';
  }

  /** Get pill background color based on state */
  private getPillBackgroundColor(
    state: 'completed' | 'current' | 'incomplete' | 'errored',
    segmentStatus: SegmentStatus
  ): string {
    const colors = {
      completed: '#7CCFA4',
      current: segmentStatus === 'errored' ? '#FDECEC' : '#E7F3F4',
      incomplete: '#BFBFBF',
      errored: '#F5A3A3',
    };
    return colors[state];
  }

  /** Generate ARIA label for individual pill */
  private generatePillAriaLabel(
    displayLabel: string,
    sectionNumber: number,
    state: 'completed' | 'current' | 'incomplete' | 'errored'
  ): string {
    const stateText = {
      completed: 'completed',
      current: 'current section',
      incomplete: 'incomplete',
      errored: 'errored',
    }[state];
    return `Section ${sectionNumber} (${displayLabel}), ${stateText}`;
  }

  /** Validate all input values */
  private validate(): boolean {
    const segmentsList = this.segments();
    const currentKey = this.currentSegment();

    const isSegmentsArray = Array.isArray(segmentsList);
    const hasSegments = segmentsList.length > 0;
    const allSegmentsValid = segmentsList.every(
      (s) =>
        s &&
        typeof s.key === 'string' &&
        s.key.length > 0 &&
        (s.status === 'completed' || s.status === 'not-completed' || s.status === 'errored')
    );
    const uniqueKeys = new Set(segmentsList.map((s) => s.key)).size === segmentsList.length;
    const isCurrentValid = currentKey === undefined || segmentsList.some((s) => s.key === currentKey);

    return isSegmentsArray && hasSegments && allSegmentsValid && uniqueKeys && isCurrentValid;
  }
}
